import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { X } from 'lucide-react';
import { Division26ListPage } from './shared/Division26ListPage';
import { Division26CreateModal } from './shared/Division26CreateModal';
import { Division26ReviewModal } from './shared/Division26ReviewModal';
import { CircuitSchedulePreview } from './CircuitSchedulePreview';
import { panelListConfig, panelFormConfig } from './panelConfig';
import { useElectricalUrlState } from './useElectricalUrlState';

export const PanelSection = ({
  projectId, csiItem, canEditCSIItems, canManageProject,
  onItemCount,
}) => {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [reviewingPanel, setReviewingPanel] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);

  // Circuit Schedule state
  const [schedulePanel, setSchedulePanel] = useState(null);
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false);

  // URL state
  const {
    urlView, urlPanelId, urlModal, urlReview, urlEdit,
    setScheduleView, setEditModal, setReviewOpen,
    clearReview, clearScheduleView, clearEditModal,
    setScheduleEdit, clearScheduleEdit,
  } = useElectricalUrlState();

  const isUserAction = useRef(false);
  const urlRestored = useRef(false);

  const csiItemId = csiItem?.id;

  useEffect(() => {
    if (onItemCount) onItemCount(panels.length);
  }, [panels.length, onItemCount]);

  const fetchPanels = useCallback(async () => {
    if (!projectId) return;
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}${panelListConfig.apiBase}/${projectId}${params}`);
      setPanels(res.data);
    } catch {
      toast.error('Failed to load panels');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  const fetchManufacturers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}${panelListConfig.manufacturerApi}`);
      setManufacturers(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchPanels(); fetchManufacturers(); }, [fetchPanels, fetchManufacturers]);

  // ── Filters ──
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [voltageFilter, setVoltageFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [mfrFilter, setMfrFilter] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  const uniqueTypes = useMemo(() => [...new Set(panels.map(p => p.panelType).filter(Boolean))].sort(), [panels]);
  const uniqueLocations = useMemo(() => [...new Set(panels.map(p => p.location).filter(Boolean))].sort(), [panels]);
  const uniqueVoltages = useMemo(() => [...new Set(panels.map(p => p.voltage).filter(Boolean))].sort(), [panels]);
  const uniquePhases = useMemo(() => [...new Set(panels.map(p => p.phase).filter(Boolean))].sort(), [panels]);
  const uniqueMfrs = useMemo(() => [...new Set(panels.map(p => p.manufacturerName).filter(Boolean))].sort(), [panels]);

  const filteredPanels = useMemo(() => {
    let result = panels;
    if (typeFilter) result = result.filter(p => p.panelType === typeFilter);
    if (locationFilter) result = result.filter(p => p.location === locationFilter);
    if (voltageFilter) result = result.filter(p => p.voltage === voltageFilter);
    if (phaseFilter) result = result.filter(p => p.phase === phaseFilter);
    if (mfrFilter) result = result.filter(p => p.manufacturerName === mfrFilter);
    return result;
  }, [panels, typeFilter, locationFilter, voltageFilter, phaseFilter, mfrFilter]);

  const hasFilters = typeFilter || locationFilter || voltageFilter || phaseFilter || mfrFilter;
  const clearFilters = () => { setTypeFilter(''); setLocationFilter(''); setVoltageFilter(''); setPhaseFilter(''); setMfrFilter(''); };

  const makeDropdownProps = (key) => ({
    open: openDropdown === key,
    onOpenChange: (open) => setOpenDropdown(prev => open ? key : (prev === key ? null : prev)),
    modal: false,
  });

  // URL → State restore (runs once after panels load)
  useEffect(() => {
    if (loading || urlRestored.current || isUserAction.current) return;
    if (!urlPanelId || panels.length === 0) return;
    urlRestored.current = true;

    const panel = panels.find(p => p.id === urlPanelId);
    if (!panel) return;

    if (urlView === 'schedule') {
      setSchedulePanel(panel);
      if (urlEdit === 'true') {
        setScheduleEditOpen(true);
      }
    } else if (urlModal === 'edit') {
      setEditingPanel(panel);
      setShowModal(true);
    }
  }, [loading, panels, urlPanelId, urlView, urlModal, urlEdit]);

  // User actions → URL updates
  const handleScheduleClick = (item) => {
    isUserAction.current = true;
    setSchedulePanel(item);
    setScheduleView(item.id);
    setTimeout(() => { isUserAction.current = false; }, 0);
  };

  const handleScheduleBack = () => {
    isUserAction.current = true;
    clearScheduleView();
    setSchedulePanel(null);
    setScheduleEditOpen(false);
    setTimeout(() => { isUserAction.current = false; }, 0);
  };

  const handleEditOpen = () => {
    isUserAction.current = true;
    setScheduleEditOpen(true);
    setScheduleEdit();
    setTimeout(() => { isUserAction.current = false; }, 0);
  };

  const handleEditClose = () => {
    isUserAction.current = true;
    setScheduleEditOpen(false);
    clearScheduleEdit();
    setTimeout(() => { isUserAction.current = false; }, 0);
  };

  const handlePanelEditClick = (item) => {
    isUserAction.current = true;
    setEditingPanel(item);
    setShowModal(true);
    setEditModal(item.id);
    setTimeout(() => { isUserAction.current = false; }, 0);
  };

  const handlePanelEditClose = (open) => {
    if (!open) {
      isUserAction.current = true;
      setShowModal(false);
      setEditingPanel(null);
      clearEditModal();
      setTimeout(() => { isUserAction.current = false; }, 0);
    }
  };

  const handleSave = async (formData, existingItem) => {
    const payload = { ...formData, csiItemId };
    if (payload.busRating !== '' && payload.busRating != null) payload.busRating = parseFloat(payload.busRating);
    else payload.busRating = null;
    if (payload.mainBreaker !== '' && payload.mainBreaker != null) payload.mainBreaker = parseFloat(payload.mainBreaker);
    else payload.mainBreaker = null;
    if (payload.circuits !== '' && payload.circuits != null) payload.circuits = parseInt(payload.circuits, 10);
    else payload.circuits = null;

    if (existingItem) {
      await axios.put(`${API}${panelListConfig.apiBase}/${projectId}/${existingItem.id}`, payload);
      toast.success('Panel updated');
    } else {
      await axios.post(`${API}${panelListConfig.apiBase}/${projectId}`, payload);
      toast.success('Panel created');
    }
    fetchPanels();
  };

  const handleDelete = async (panelId) => {
    await axios.delete(`${API}${panelListConfig.apiBase}/${projectId}/${panelId}`);
    setPanels(prev => prev.filter(p => p.id !== panelId));
  };

  const handleBulkDelete = async (ids) => {
    const res = await axios.post(`${API}${panelListConfig.apiBase}/${projectId}/bulk-delete`, { ids });
    setPanels(prev => prev.filter(p => !ids.includes(p.id)));
    return res.data.deleted || 0;
  };

  const handleCreateManufacturer = async (name) => {
    const res = await axios.post(`${API}${panelListConfig.manufacturerApi}`, { name });
    setManufacturers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
    return res.data;
  };

  // If viewing circuit schedule, render preview instead of list
  if (schedulePanel) {
    return (
      <CircuitSchedulePreview
        panel={schedulePanel}
        projectId={projectId}
        manufacturers={manufacturers}
        onBack={handleScheduleBack}
        editOpen={scheduleEditOpen}
        onEditOpen={handleEditOpen}
        onEditClose={handleEditClose}
      />
    );
  }

  // ── Filter Bar ──
  const filterBar = (
    <>
      <Select value={typeFilter} onValueChange={(val) => setTypeFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('type')}>
        <SelectTrigger className="w-32 h-8 text-xs" data-testid="panel-filter-type">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Types</SelectItem>
          {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={locationFilter} onValueChange={(val) => setLocationFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('location')}>
        <SelectTrigger className="w-36 h-8 text-xs" data-testid="panel-filter-location">
          <SelectValue placeholder="All Locations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Locations</SelectItem>
          {uniqueLocations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={voltageFilter} onValueChange={(val) => setVoltageFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('voltage')}>
        <SelectTrigger className="w-32 h-8 text-xs" data-testid="panel-filter-voltage">
          <SelectValue placeholder="All Voltages" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Voltages</SelectItem>
          {uniqueVoltages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={phaseFilter} onValueChange={(val) => setPhaseFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('phase')}>
        <SelectTrigger className="w-28 h-8 text-xs" data-testid="panel-filter-phase">
          <SelectValue placeholder="All Phases" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Phases</SelectItem>
          {uniquePhases.map(p => <SelectItem key={p} value={p}>Phase {p}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={mfrFilter} onValueChange={(val) => setMfrFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('manufacturer')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="panel-filter-manufacturer">
          <SelectValue placeholder="All Manufacturers" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Manufacturers</SelectItem>
          {uniqueMfrs.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="panel-clear-filters-btn">
          <X className="h-3.5 w-3.5 mr-1" />Clear
        </Button>
      )}
    </>
  );

  return (
    <>
      <Division26ListPage
        config={panelListConfig}
        items={panels}
        loading={loading}
        projectId={projectId}
        csiItem={csiItem}
        canEditCSIItems={canEditCSIItems}
        canManageProject={canManageProject}
        onCreateClick={() => { setEditingPanel(null); setShowModal(true); }}
        onEditClick={handlePanelEditClick}
        onReviewClick={(item) => setReviewingPanel(item)}
        onScheduleClick={handleScheduleClick}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        filterBar={filterBar}
        filteredItems={filteredPanels}
        showShare={false}
      />

      <Division26CreateModal
        open={showModal}
        onOpenChange={handlePanelEditClose}
        config={panelFormConfig}
        editItem={editingPanel}
        onSave={handleSave}
        manufacturers={manufacturers}
        onCreateManufacturer={handleCreateManufacturer}
        projectId={projectId}
      />

      <Division26ReviewModal
        open={!!reviewingPanel}
        onOpenChange={(open) => { if (!open) setReviewingPanel(null); }}
        config={panelFormConfig}
        item={reviewingPanel}
        manufacturers={manufacturers}
      />
    </>
  );
};
